import {
  type InfiniteData,
  infiniteQueryOptions,
  mutationOptions,
} from "@tanstack/react-query";
import {
  CHANGE_GALLERY_ITEM_FAVORITE_STATUS,
  GET_GALLERY_ITEMS,
  GET_UNOPENED_ITEM,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import type {
  GalleryItem,
  GalleryItemsResponse,
  GalleryKind,
  GalleryStatus,
  ToggleFavoriteVariables,
  UnopenedItemsResponse,
} from "./types";

export const GALLERY_PAGE_SIZE = 20;

export type GalleryPage = {
  items: GalleryItem[];
  nextSkip: number;
};

// BE は favorite / non_favorite を別配列で返すが、ここで 1 本のフラット配列に統合する。
// 2 配列のまま保持するといいねトグル時にアイテムが配列間を移動して順番がジャンプするため、
// 単一配列にしてフラグだけ書き換えられるようにしている。
const normalizeOpened = (
  data: GalleryItemsResponse | undefined,
  skip: number,
): GalleryPage => {
  if (!data) return { items: [], nextSkip: skip };
  const favorites = (data.favorite_gallery_list ?? []).map((entry) => ({
    userId: entry.gallery_user.user_id,
    userName: entry.gallery_user.user_name,
    avatarId: entry.gallery_user.ava_id,
    age: entry.gallery_user.age,
    fileId: entry.opened_content.file_id,
    isFavorite: true,
    isOpened: true,
  }));
  const others = (data.non_favorite_gallery_list ?? []).map((entry) => ({
    userId: entry.gallery_user.user_id,
    userName: entry.gallery_user.user_name,
    avatarId: entry.gallery_user.ava_id,
    age: entry.gallery_user.age,
    fileId: entry.opened_content.file_id,
    isFavorite: false,
    isOpened: true,
  }));
  const items = [...favorites, ...others];
  return { items, nextSkip: skip + items.length };
};

// 未開封アイテムのフラット配列を normalize する
const normalizeUnopened = (
  data: UnopenedItemsResponse | undefined,
  skip: number,
): GalleryPage => {
  if (!data) return { items: [], nextSkip: skip };
  const items = data.map((e) => ({
    userId: e.user.user_id,
    userName: e.user.user_name,
    avatarId: e.user.ava_id,
    age: e.user.age,
    fileId: e.unopened_content.file_id,
    isFavorite: false,
    isOpened: false,
    views: e.unopened_content.views,
    favorites: e.unopened_content.favorites,
    duration: e.unopened_content.duration,
  }));
  return { items, nextSkip: skip + items.length };
};

export const galleryItemsInfiniteQueryOptions = (
  kind: GalleryKind,
  status: GalleryStatus,
) => {
  const isImage = kind === "image";
  return infiniteQueryOptions({
    queryKey: queryKeys.gallery.items(kind, status),
    queryFn: async ({ pageParam }) => {
      if (status === "opened") {
        const raw = await apiClient<GalleryItemsResponse>(GET_GALLERY_ITEMS, {
          is_image: isImage,
          take: GALLERY_PAGE_SIZE,
          skip: pageParam,
        });
        return normalizeOpened(raw, pageParam);
      }
      // 未開封アイテムを取得
      const raw = await apiClient<UnopenedItemsResponse>(GET_UNOPENED_ITEM, {
        is_image: isImage,
        take: GALLERY_PAGE_SIZE,
        skip: pageParam,
      });
      return normalizeUnopened(raw, pageParam);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.items.length < GALLERY_PAGE_SIZE ? undefined : lastPage.nextSkip,
  });
};

type GalleryInfiniteData = InfiniteData<GalleryPage, number>;

const mapPages = (
  data: GalleryInfiniteData,
  fileId: string,
  nextIsFavorite: boolean,
): GalleryInfiniteData => {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) =>
        item.fileId === fileId ? { ...item, isFavorite: nextIsFavorite } : item,
      ),
    })),
  };
};

export const toggleGalleryFavoriteMutationOptions = mutationOptions({
  mutationFn: (vars: ToggleFavoriteVariables) =>
    apiClient<unknown>(CHANGE_GALLERY_ITEM_FAVORITE_STATUS, {
      file_id: vars.fileId,
      is_favorite: vars.nextIsFavorite,
    }),
  onMutate: async (vars) => {
    // お気に入りトグルは開封済み専用なので status は常に "opened"
    const queryKey = queryKeys.gallery.items(vars.kind, vars.status);
    await queryClient.cancelQueries({ queryKey });
    const previous = queryClient.getQueryData<GalleryInfiniteData>(queryKey);
    if (!previous) return { previous };

    queryClient.setQueryData<GalleryInfiniteData>(
      queryKey,
      mapPages(previous, vars.fileId, vars.nextIsFavorite),
    );

    return { previous };
  },
  onError: (_err, vars, context) => {
    if (context?.previous) {
      queryClient.setQueryData(
        queryKeys.gallery.items(vars.kind, vars.status),
        context.previous,
      );
    }
  },
});
